// Contracts — work.weern.com EARNER cabinet (2026-06-27). The money-first
// gift-card photographer cabinet (recruited contractors). Two endpoints the
// earner cabinet adds on top of the shared photographer/generator surface:
//   POST /api/work/assistant — the in-cabinet AI helper (Gemini on the Worker).
//   GET  /api/work/referral  — the contractor's invite link + invited friends.
import { z } from 'zod';
import { WorkIcfViewSchema } from './photographer';

// ── assistant ────────────────────────────────────────────────────────────────
// One chat turn. `context` is a compact, client-built stats block (balance,
// frames toward goal, …) the Worker folds into the system prompt; `history` is
// the trailing conversation so the model has continuity. Money/rules framing is
// owned by the Worker, not the client.
export const WorkAssistantTurnSchema = z.object({
  role: z.enum(['user', 'assistant']),
  text: z.string().max(4000),
});
export type WorkAssistantTurn = z.infer<typeof WorkAssistantTurnSchema>;

export const WorkAssistantReqSchema = z.object({
  message: z.string().trim().min(1).max(2000),
  history: z.array(WorkAssistantTurnSchema).max(12).default([]),
  context: z.string().max(2000).default(''),
});
export type WorkAssistantReq = z.infer<typeof WorkAssistantReqSchema>;

export const WorkAssistantResSchema = z.object({
  reply: z.string(),
  // true → the contractor hit the per-hour/per-day reuse limit; the client shows
  // a localized "slow down" notice instead of a model reply. (No LLM call spent.)
  limited: z.boolean().default(false),
});
export type WorkAssistantRes = z.infer<typeof WorkAssistantResSchema>;

// GET /api/work/assistant/history — recent turns in the same-conversation window
// (server-side log), so the cabinet restores the chat on reopen. Newest-last.
export const WorkAssistantHistoryResSchema = z.object({
  messages: z.array(WorkAssistantTurnSchema),
});
export type WorkAssistantHistoryRes = z.infer<typeof WorkAssistantHistoryResSchema>;

// ── referral ─────────────────────────────────────────────────────────────────
// One invited friend + their live progress toward the credit threshold (100
// accepted uploads → the referrer earns $5). `credited` = uploads ≥ threshold.
export const WorkReferralFriendSchema = z.object({
  name: z.string(),
  uploads: z.number().int().nonnegative(),
  joined_at: z.string(),
  credited: z.boolean(),
});
export type WorkReferralFriend = z.infer<typeof WorkReferralFriendSchema>;

// POST /api/work/onboarded — mark the caller's one-time onboarding flow complete.
export const WorkOnboardedResSchema = z.object({ ok: z.boolean() });
export type WorkOnboardedRes = z.infer<typeof WorkOnboardedResSchema>;

// ── consent (ICF e-signature) ─────────────────────────────────────────────────
// POST /api/work/consent — the earner types their full name as an e-signature on
// the onboarding ICF step. The Worker resolves the jurisdiction document
// (CF-IPCountry) itself; the client supplies the signature + light device
// evidence. One immutable `work_icf_log` row per acceptance (migrations/0019).
export const WorkConsentReqSchema = z.object({
  signature_name: z.string().trim().min(2).max(200),   // the typed e-signature
  language: z.string().trim().max(12).default(''),     // UI language at signing
  device_kind: z.string().trim().max(40).default(''),  // mobile | tablet | desktop
  browser: z.string().trim().max(80).default(''),
  os: z.string().trim().max(80).default(''),
  timezone: z.string().trim().max(80).default(''),
});
export type WorkConsentReq = z.infer<typeof WorkConsentReqSchema>;

export const WorkConsentResSchema = z.object({
  ok: z.boolean(),
  consent_id: z.string(),                  // 'CR-<hex>' audit id
  icf: WorkIcfViewSchema.nullable(),       // the document the signature is bound to
});
export type WorkConsentRes = z.infer<typeof WorkConsentResSchema>;

export const WorkReferralResSchema = z.object({
  code: z.string(),                 // the caller's referral code
  link: z.string(),                 // full invite link carrying ?ref=<code>
  per_friend_usd: z.number(),       // reward per qualifying friend (5)
  threshold: z.number().int(),      // uploads a friend needs (100)
  invited: z.number().int().nonnegative(),
  earned_usd: z.number(),           // per_friend_usd × credited friends
  friends: z.array(WorkReferralFriendSchema),
});
export type WorkReferralRes = z.infer<typeof WorkReferralResSchema>;
